import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  BarChart2,
  Loader2,
  ClipboardList,
  Users,
  TrendingUp,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Link2,
  Send,
  Code2,
  MessageCircle,
  Settings2,
} from 'lucide-react';
import api from '../services/api';
// Forms engine (merged 2026-09-28): how a form is sent on WhatsApp / by
// Automation, and the account-wide WhatsApp form settings.
import {
  FormWhatsAppPreview,
  FormsSettingsPanel,
} from '../components/Automation/FormWhatsAppSettings';
import PreChatFormSettings from './PreChatFormSettings';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FormField,
  FormPage,
  FormSection,
  flattenPages,
  pagesFromForm,
  pagesForStorage,
  makeDefaultPages,
} from './preChatFormStructure';
// 2026-09-29: the question list + side panel (Docs/mockups/form-builder-redesign.html, option A).
import PreChatFormBuilder, {
  BuilderState,
  builderFromPages,
  builderProblems,
  pagesFromBuilder,
} from './PreChatFormBuilder';
import PreChatFormPublishDialog from './PreChatFormPublishDialog';
import PreChatFormApiDialog from './PreChatFormApiDialog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PreChatForm {
  id: number;
  title: string;
  subtitle: string;
  fields: FormField[];
  pages?: FormPage[];
  channel_id?: number | null;
  is_enabled: boolean;
  status?: 'draft' | 'published' | string;
  is_required: boolean;
  show_message_box: boolean;
  logo_url?: string;
  primary_color: string;
  enable_business_hours_check: boolean;
  offline_message: string;
  total_submissions: number;
  conversion_rate: number;
  created_at: string;
  public_slug?: string | null;
  whatsapp_flow_id?: string | null;
  whatsapp_channel_id?: number | null;
  // Forms engine: key, type (registration / profile / survey), repeat rules,
  // WhatsApp collector, show_in_widget ... (app/config/general_form_defaults.py)
  options?: Record<string, any>;
}


const DEFAULT_FORM = {
  title: "Let's get you connected!",
  subtitle: 'Please provide some details to help us assist you better.',
  pages: [] as FormPage[],
  status: 'draft' as 'draft' | 'published',
  is_enabled: true,
  is_required: false,
  show_message_box: true,
  enable_business_hours_check: false,
  offline_message:
    "We're currently offline. Please leave your details and we'll get back to you.",
  primary_color: '#1f93ff',
  options: {} as Record<string, any>,
  // 2026-09-28 (Phase 0): which website inbox shows it; null = all inboxes.
  channel_id: null as number | null,
};

function apiProblems(err: any): string | undefined {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  const problems: string[] | undefined = detail?.problems;
  return problems?.length ? problems.join(' ') : undefined;
}

const SELECT_CLS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function formPageCount(form: PreChatForm): number {
  return pagesFromForm(form).length;
}


// ─── Page ─────────────────────────────────────────────────────────────────────

const PreChatFormsPage: React.FC = () => {
  const navigate = useNavigate();

  const [forms, setForms] = useState<PreChatForm[]>([]);
  const [loading, setLoading] = useState(true);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState<{
    title: string;
    subtitle: string;
    primary_color: string;
    pages: FormPage[];
  } | null>(null);
  const [selectedForm, setSelectedForm] = useState<PreChatForm | null>(null);
  const [formToDelete, setFormToDelete] = useState<PreChatForm | null>(null);
  const [publishForm, setPublishForm] = useState<PreChatForm | null>(null);
  const [publishMode, setPublishMode] = useState<'publish' | 'send'>('publish');
  const [publishOpen, setPublishOpen] = useState(false);
  const [apiForm, setApiForm] = useState<PreChatForm | null>(null);
  const [apiOpen, setApiOpen] = useState(false);

  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  // 2026-09-29 (builder redesign): the questions as one list; saved as pages.
  const [builder, setBuilder] = useState<BuilderState>(() => builderFromPages(makeDefaultPages()));
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [savedFieldIds, setSavedFieldIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [editorTab, setEditorTab] = useState<'questions' | 'settings'>('questions');
  const builderPages = useMemo(() => pagesFromBuilder(builder), [builder]);
  const [waPreviewForm, setWaPreviewForm] = useState<PreChatForm | null>(null);
  // Website inboxes, for "which inbox shows this form" (Phase 0).
  const [websiteInboxes, setWebsiteInboxes] = useState<{ id: number; name: string }[]>([]);
  const inboxName = (channelId?: number | null) =>
    channelId ? websiteInboxes.find((i) => i.id === channelId)?.name || `Inbox #${channelId}` : 'All website inboxes';
  const isRegistration = (f: { options?: Record<string, any> }) =>
    (f.options?.kind || (f.options?.marks_registered ? 'registration' : '')) === 'registration';
  const otherRegistration = forms.find(
    (f) => f.id !== selectedForm?.id && isRegistration(f) && !f.options?.archived_at,
  );

  const activeForms = forms.filter((f) => f.is_enabled).length;
  const totalSubmissions = forms.reduce((sum, f) => sum + (f.total_submissions || 0), 0);
  const avgConversion =
    forms.length > 0
      ? Math.round(forms.reduce((sum, f) => sum + (f.conversion_rate || 0), 0) / forms.length)
      : 0;

  const fetchData = async () => {
    try {
      setLoading(true);
      const formsRes = await api.get('/pre-chat-forms/');
      setForms(formsRes.data || []);
      api
        .get('/inboxes/')
        .then((res) => {
          const raw = res.data?.inboxes ?? res.data ?? [];
          setWebsiteInboxes(
            (Array.isArray(raw) ? raw : [])
              .filter((i: any) => String(i.channel_type || '').toLowerCase() === 'website')
              .map((i: any) => ({ id: i.id, name: i.name })),
          );
        })
        .catch(() => setWebsiteInboxes([]));
    } catch {
      toast({ title: 'Failed to load forms', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  const loadBuilder = (pages: FormPage[]) => {
    const next = builderFromPages(pages);
    setBuilder(next);
    setSavedFieldIds(new Set(flattenPages(pages).map((f) => f.id)));
    setSelectedKey(next.items.find((i) => i.kind === 'question')?.key || null);
  };

  const resetForm = () => {
    const pages = makeDefaultPages();
    setFormData({ ...DEFAULT_FORM, pages, options: {}, channel_id: null });
    setEditorTab('questions');
    loadBuilder(pages);
  };

  const openCreate = () => {
    setSelectedForm(null);
    resetForm();
    setFormDialogOpen(true);
  };

  const handleEditForm = (form: PreChatForm) => {
    setSelectedForm(form);
    const pages = pagesFromForm(form);
    setFormData({
      title: form.title,
      subtitle: form.subtitle || '',
      pages,
      status: (form.status === 'draft' ? 'draft' : 'published') as 'draft' | 'published',
      is_enabled: form.is_enabled,
      is_required: form.is_required,
      show_message_box: form.show_message_box,
      enable_business_hours_check: form.enable_business_hours_check,
      offline_message:
        form.offline_message ||
        "We're currently offline. Please leave your details and we'll get back to you.",
      primary_color: form.primary_color || '#1f93ff',
      options: form.options || {},
      channel_id: form.channel_id ?? null,
    });
    setEditorTab('questions');
    loadBuilder(pages);
    setFormDialogOpen(true);
  };

  const scrubTree = (pages: FormPage[]): FormPage[] =>
    pages.map((page) => ({
      id: page.id,
      title: page.title || undefined,
      sections: (page.sections || []).map((section) => {
        const sec: FormSection = {
          id: section.id,
          title: section.page_body ? '' : section.title || 'Section',
          fields: (section.fields || []).map((field) => {
            const f: FormField = {
              id: field.id,
              type: field.type,
              label: field.label.trim(),
              required: !!field.required,
              options: Array.isArray(field.options) ? field.options.map((o) => o.trim()).filter(Boolean) : [],
            };
            if (field.placeholder) f.placeholder = field.placeholder;
            if (field.validation && Object.keys(field.validation).length) f.validation = field.validation;
            if (field.show_if?.field_id) {
              f.show_if = { field_id: field.show_if.field_id, equals: field.show_if.equals };
            }
            // Forms engine keys -- not edited here yet, but kept on save.
            if (Array.isArray(field.conditions) && field.conditions.length) f.conditions = field.conditions;
            if (field.save_to_profile === false) f.save_to_profile = false;
            return f;
          }),
        };
        if (section.show_if?.field_id) {
          sec.show_if = { field_id: section.show_if.field_id, equals: section.show_if.equals };
        }
        if (section.page_body) sec.page_body = true;
        return sec;
      }),
    }));

  const handleSaveForm = async (asDraft: boolean) => {
    const nextStatus: 'draft' | 'published' = asDraft ? 'draft' : 'published';

    if (!formData.title.trim() && !asDraft) {
      setEditorTab('settings');
      toast({ title: 'Give the form a title before publishing', variant: 'destructive' });
      return;
    }
    const problem = builderProblems(builder);
    if (problem) {
      setEditorTab('questions');
      setSelectedKey(problem.key);
      toast({ title: problem.message, variant: 'destructive' });
      return;
    }
    const stored = scrubTree(pagesForStorage(builderPages));
    const fields = flattenPages(stored);
    if (!asDraft && fields.length === 0) {
      setEditorTab('questions');
      toast({ title: 'Add at least one question before publishing', variant: 'destructive' });
      return;
    }

    const payload: Record<string, any> = {
      title: formData.title.trim() || (asDraft ? 'Untitled draft' : formData.title),
      subtitle: formData.subtitle || '',
      status: nextStatus,
      is_enabled: formData.is_enabled,
      is_required: formData.is_required,
      show_message_box: formData.show_message_box,
      enable_business_hours_check: formData.enable_business_hours_check,
      offline_message: formData.offline_message,
      primary_color: formData.primary_color || '#1f93ff',
      pages: stored,
      fields,
      options: formData.options || {},
      channel_id: formData.channel_id ?? null,
    };

    setSaving(true);
    try {
      if (selectedForm) {
        await api.put(`/pre-chat-forms/${selectedForm.id}`, payload);
        toast({ title: asDraft ? 'Draft saved' : 'Form published / updated' });
      } else {
        await api.post('/pre-chat-forms/', payload);
        toast({ title: asDraft ? 'Draft created' : 'Form created and published' });
      }
      setFormDialogOpen(false);
      setSelectedForm(null);
      resetForm();
      fetchData();
    } catch (err: any) {
      toast({
        title: 'Failed to save form',
        description: apiProblems(err),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return;
    try {
      await api.delete(`/pre-chat-forms/${formToDelete.id}`);
      toast({ title: 'Form deleted successfully' });
      fetchData();
    } catch (err: any) {
      // e.g. automation rules still send it, or someone is answering it on WhatsApp
      toast({ title: 'Failed to delete form', description: apiProblems(err), variant: 'destructive' });
    } finally {
      setFormToDelete(null);
    }
  };

  const openPublish = (form: PreChatForm, mode: 'publish' | 'send' = 'publish') => {
    setPublishForm(form);
    setPublishMode(mode);
    setPublishOpen(true);
  };

  const openPreview = (form: PreChatForm | typeof formData) => {
    const pages =
      'pages' in form && Array.isArray(form.pages) && form.pages.length
        ? form.pages
        : pagesFromForm(form as PreChatForm);
    setPreviewForm({
      title: form.title,
      subtitle: form.subtitle || '',
      primary_color: form.primary_color || '#1f93ff',
      pages,
    });
    setPreviewOpen(true);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 py-4 px-4 md:py-6 lg:px-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Pre-Chat Forms</h1>
            <p className="text-sm text-muted-foreground">
              Collect visitor information before starting a conversation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={openCreate} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Create Form
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Total Forms</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{forms.length}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <ClipboardList className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            <span className="text-emerald-600 font-medium">{activeForms} active</span>
            <span className="mx-1">·</span>
            {forms.length - activeForms} disabled
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-emerald-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Active Forms</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{activeForms}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Currently collecting leads
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-amber-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Total Submissions</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">
              {totalSubmissions.toLocaleString()}
            </CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/10">
                <Users className="h-4 w-4 text-amber-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Across all forms
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-violet-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Avg Conversion</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{avgConversion}%</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/10">
                <TrendingUp className="h-4 w-4 text-violet-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Average across all forms
          </CardFooter>
        </Card>
      </div>

      <Tabs defaultValue="forms" className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="forms" className="gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />
            Forms
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            WhatsApp settings
          </TabsTrigger>
        </TabsList>

        {/* ── WhatsApp settings (forms engine, account-wide) ──────────────── */}
        <TabsContent value="whatsapp">
          <FormsSettingsPanel />
        </TabsContent>

        <TabsContent value="forms">
          {/* Cards */}
          {forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
                <ClipboardList className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="font-semibold mb-1">No pre-chat forms yet</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-xs">
                Create a form to collect visitor information before they start a conversation.
              </p>
              <Button onClick={openCreate} className="gap-2">
                <Plus className="h-4 w-4" /> Create Your First Form
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {forms.map((form) => (
                <Card
                  key={form.id}
                  className="flex flex-col overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card"
                >
                  <div
                    className={cn(
                      'h-1 w-full shrink-0',
                      form.status === 'draft'
                        ? 'bg-amber-500'
                        : form.is_enabled
                          ? 'bg-emerald-500'
                          : 'bg-muted-foreground/30',
                    )}
                  />

                  <CardHeader className="pb-2 pt-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold leading-snug line-clamp-1">
                          {form.title}
                        </CardTitle>
                        <CardDescription className="text-xs mt-0.5 line-clamp-1">
                          {form.subtitle || 'Pre-chat form'}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {form.status === 'draft' && (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-5 px-1.5 border-amber-600/60 bg-amber-950/40 text-amber-300"
                          >
                            Draft
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] h-5 px-1.5 gap-1',
                            form.is_enabled
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                              : 'border-border bg-muted/40 text-muted-foreground',
                          )}
                        >
                          {form.is_enabled ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                          {form.is_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 space-y-3 pt-0 pb-3">
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { label: 'Pages', value: String(formPageCount(form)) },
                        {
                          label: 'Submissions',
                          value: (form.total_submissions || 0).toLocaleString(),
                        },
                      ].map(({ label, value }) => (
                        <div
                          key={label}
                          className="rounded-lg bg-muted/50 px-2 py-1.5 text-center"
                        >
                          <p className="text-xs font-semibold tabular-nums">{value}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-mono text-[10px] text-muted-foreground"
                        title="Key used by Automation rules"
                      >
                        {form.options?.key || `form_${form.id}`}
                      </span>
                      {form.options?.show_in_widget !== false && (
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                          {inboxName(form.channel_id)}
                        </span>
                      )}
                      {isRegistration(form) && (
                        <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                          Registration
                        </span>
                      )}
                      {form.options?.show_in_widget === false && (
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                          Not in widget
                        </span>
                      )}
                      {form.public_slug && (
                        <span className="text-[10px] bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded">
                          Web link
                        </span>
                      )}
                      {form.whatsapp_flow_id && (
                        <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded">
                          WA Flow
                        </span>
                      )}
                      <button
                        type="button"
                        className="text-[10px] bg-violet-500/15 text-violet-300 px-1.5 py-0.5 rounded hover:bg-violet-500/25"
                        onClick={() => {
                          setApiForm(form);
                          setApiOpen(true);
                        }}
                      >
                        API
                      </button>
                      <div className="ml-auto flex items-center gap-1">
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-border/50 shrink-0"
                          style={{ backgroundColor: form.primary_color }}
                          title={form.primary_color}
                        />
                      </div>
                    </div>
                  </CardContent>

                  <Separator />

                  <CardFooter className="flex items-center gap-1 pt-2.5 pb-2.5 px-4 flex-wrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => handleEditForm(form)}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => openPreview(form)}
                    >
                      <Eye className="h-3 w-3" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => navigate(`/dashboard/pre-chat-forms/${form.id}/responses`)}
                    >
                      <BarChart2 className="h-3 w-3" /> Responses
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => openPublish(form, 'publish')}
                    >
                      <Link2 className="h-3 w-3" /> Generate / Publish…
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => {
                        setApiForm(form);
                        setApiOpen(true);
                      }}
                    >
                      <Code2 className="h-3 w-3" /> API
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1"
                      disabled={!form.whatsapp_flow_id}
                      title={
                        form.whatsapp_flow_id
                          ? 'Send published Flow'
                          : 'Publish a WhatsApp Flow first'
                      }
                      onClick={() => openPublish(form, 'send')}
                    >
                      <Send className="h-3 w-3" /> Send Flow
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      title="How it looks on WhatsApp (asked in chat)"
                      onClick={() => setWaPreviewForm(form)}
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => setFormToDelete(form)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>


      {/* Create / edit -- 2026-09-29 builder redesign (Docs/mockups/form-builder-redesign.html, option A):
          the questions as one list with a side panel; appearance, inbox and WhatsApp settings on their own tab. */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="!flex h-[92vh] w-[min(1400px,96vw)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-5 py-3 pr-14">
            <DialogTitle className="sr-only">
              {selectedForm ? 'Edit form' : 'Create form'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Questions on the left, the selected question's settings on the right.
            </DialogDescription>
            <Input
              aria-label="Form title"
              value={formData.title}
              placeholder="Untitled form"
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              className="h-9 w-[min(360px,40vw)] border-transparent bg-transparent px-2 text-base font-semibold shadow-none hover:border-border focus-visible:border-border"
            />
            <Badge variant="secondary" className="text-[11px]">
              {selectedForm?.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
            <Tabs value={editorTab} onValueChange={(v) => setEditorTab(v as 'questions' | 'settings')}>
              <TabsList>
                <TabsTrigger value="questions">Questions</TabsTrigger>
                <TabsTrigger value="settings" className="gap-1.5">
                  <Settings2 className="h-3.5 w-3.5" /> Settings
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => openPreview({ ...formData, pages: builderPages })}
              >
                <Eye className="h-4 w-4" /> Preview
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleSaveForm(true)} disabled={saving}>
                Save draft
              </Button>
              <Button size="sm" onClick={() => handleSaveForm(false)} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : selectedForm ? (
                  'Publish / update'
                ) : (
                  'Publish'
                )}
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            {editorTab === 'questions' ? (
              <PreChatFormBuilder
                state={builder}
                onChange={setBuilder}
                savedFieldIds={savedFieldIds}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                title={formData.title}
                subtitle={formData.subtitle}
                color={formData.primary_color}
              />
            ) : (
              <div className="h-full overflow-y-auto">
                {/* 2026-09-29: one simple settings page (PreChatFormSettings.tsx). */}
                <PreChatFormSettings
                  values={formData}
                  onChange={(patch) => setFormData((p) => ({ ...p, ...patch }))}
                  websiteInboxes={websiteInboxes}
                  publishedFlowId={selectedForm?.whatsapp_flow_id}
                  onPublishWhatsApp={selectedForm ? () => openPublish(selectedForm, 'publish') : undefined}
                  otherRegistration={otherRegistration?.title || null}
                  savedVersion={selectedForm ? Number(selectedForm.options?.version || 1) : undefined}
                  savedKey={selectedForm ? selectedForm.options?.key || `form_${selectedForm.id}` : undefined}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>Form Preview</DialogTitle>
            <DialogDescription>How pages and fields appear to visitors.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="px-6 py-5">
              {previewForm && (
                <Card className="overflow-hidden">
                  <div
                    className="px-4 py-3 text-white text-center"
                    style={{ backgroundColor: previewForm.primary_color }}
                  >
                    <p className="font-semibold">{previewForm.title}</p>
                  </div>
                  <CardContent className="space-y-4 p-4">
                    {previewForm.subtitle && (
                      <p className="text-sm text-muted-foreground text-center">
                        {previewForm.subtitle}
                      </p>
                    )}
                    {previewForm.pages.map((page, pi) => (
                      <div key={page.id} className="space-y-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Page {pi + 1}
                          {page.title ? ` · ${page.title}` : ''}
                        </p>
                        {(page.sections || []).map((sec) => (
                          <div key={sec.id} className="space-y-2">
                            {!sec.page_body && sec.title && (
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                {sec.title}
                              </p>
                            )}
                            {(sec.fields || []).map((field) => (
                              <div key={field.id} className="space-y-1">
                                {field.type !== 'acknowledge' && field.type !== 'note' && (
                                  <Label className="text-xs">
                                    {field.label}
                                    {field.required ? ' *' : ''}
                                  </Label>
                                )}
                                {field.type === 'note' ? (
                                  <p className="text-sm text-muted-foreground">{field.label}</p>
                                ) : field.type === 'textarea' ? (
                                  <textarea
                                    disabled
                                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    placeholder={field.placeholder}
                                  />
                                ) : field.type === 'select' ? (
                                  <select disabled className={SELECT_CLS}>
                                    <option>Select…</option>
                                    {(field.options || []).map((o) => (
                                      <option key={o}>{o}</option>
                                    ))}
                                  </select>
                                ) : field.type === 'checkbox' || field.type === 'acknowledge' ? (
                                  <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" disabled className="h-4 w-4" />
                                    {field.label}
                                  </label>
                                ) : field.type === 'rating' ? (
                                  <div className="flex gap-0.5 text-amber-400 text-xl">
                                    {Array.from({
                                      length: field.validation?.max_stars || 5,
                                    }).map((_, i) => (
                                      <span key={i}>☆</span>
                                    ))}
                                  </div>
                                ) : (
                                  <Input
                                    disabled
                                    type={
                                      field.type === 'datetime'
                                        ? 'datetime-local'
                                        : field.type === 'number'
                                          ? 'number'
                                          : field.type === 'email'
                                            ? 'email'
                                            : field.type === 'tel'
                                              ? 'tel'
                                              : field.type === 'date' || field.type === 'time'
                                                ? field.type
                                                : 'text'
                                    }
                                    placeholder={field.placeholder}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp preview (forms engine) */}
      <FormWhatsAppPreview form={waPreviewForm} onClose={() => setWaPreviewForm(null)} />

      {/* Delete */}
      <AlertDialog
        open={!!formToDelete}
        onOpenChange={(open) => !open && setFormToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{formToDelete?.title}</strong>? This cannot
              be undone and all its responses (widget, web link, app and WhatsApp) will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFormToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PreChatFormPublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        form={publishForm}
        mode={publishMode}
        onDone={fetchData}
      />
      <PreChatFormApiDialog
        open={apiOpen}
        onOpenChange={setApiOpen}
        formId={apiForm?.id ?? null}
        formTitle={apiForm?.title}
      />
    </div>
  );
};

export default PreChatFormsPage;
